import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MarketplaceLayout from './MarketplaceLayout';
import ProductList from './ProductList';
import ArtistRegistration from './ArtistRegistration';
import SignIn from '../auth/SignIn';
import SignUp from '../auth/SignUp';
import Cart from './Cart';
import ProductDetail from './ProductDetail';
import ProductDetails from './ProductDetails'; // New enhanced product details component
import Withdraw from './Withdraw';
import Checkout from './Checkout';
import OrderSuccess from './OrderSuccess';
import OrderConfirmation from './OrderConfirmation';
import OrderHistory from './OrderHistory';
import OrderDetails from './OrderDetails';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import PaymentProcessing from './PaymentProcessing';
import ArtistRequestsDashboard from '../admin/ArtistRequestsDashboard';
import MarketplaceSearch from './MarketplaceSearch'; // New search component
import ArtistProfile from './ArtistProfile'; // New artist profile component
import Wishlist from './Wishlist'; // New wishlist component
import AuthGuard from '../auth/AuthGuard'; // New auth guard component
import MFASetup from '../auth/MFASetup'; // New MFA setup component
import SessionManager from '../auth/SessionManager'; // New session manager component
import ProfilePage from './ProfilePage'; // New profile page component
import ProductCreateForm from './ProductCreateForm'; // New product creation form
import EditProduct from './EditProduct'; // New product editing component
import ArtistEarningsDashboard from './ArtistEarningsDashboard'; // New artist earnings dashboard
import ArtistRequestForm from './ArtistRequestForm'; // Artist request form
import PaymentCredentialsManager from './PaymentCredentialsManager'; // Payment API credentials manager
import ArtistApprovalDashboard from '../admin/ArtistApprovalDashboard'; // Artist approval dashboard
import { ROLES } from '../../firebase.marketplace';
import { HelmetProvider } from 'react-helmet-async';
import AdminTools from '../admin/AdminTools'; // Admin tools component
import SuperAdminSetup from '../admin/SuperAdminSetup'; // Super admin setup component
import AdminSetupButton from '../admin/AdminSetupButton'; // Direct admin setup button
import DirectAdminSetup from '../admin/DirectAdminSetup'; // Most direct admin setup approach
import QuickAdminSetup from '../admin/QuickAdminSetup'; // Quick admin setup with user ID
import GetUserId from '../admin/GetUserId'; // Component to get user ID
import DirectAdminCreator from '../admin/DirectAdminCreator'; // Component to create admin user directly
import SimpleAdminSetup from '../admin/SimpleAdminSetup'; // Simplified admin setup
import EmergencyAdminSetup from '../admin/EmergencyAdminSetup'; // Emergency admin setup with direct user ID
import EmergencyAdminAccess from '../admin/EmergencyAdminAccess'; // New emergency admin access component
import BypassAdminDashboard from '../admin/BypassAdminDashboard'; // Special admin dashboard that works with permission restrictions
import DirectSuperAdminAccess from '../admin/DirectSuperAdminAccess'; // Direct super admin access component

// Using the new AuthGuard component instead of the old ProtectedRoute

const MarketplaceRoutes = () => {
  return (
    <HelmetProvider>
      <Routes>
        {/* Auth Routes */}
        <Route path="auth/signin" element={<MarketplaceLayout><SignIn /></MarketplaceLayout>} />
        <Route path="auth/signup" element={<MarketplaceLayout><SignUp /></MarketplaceLayout>} />

        {/* Public Routes */}
        <Route path="/" element={<MarketplaceLayout><ProductList /></MarketplaceLayout>} />
        
        {/* Product Routes - Both old and new implementations */}
        <Route path="products/:productSlug" element={<MarketplaceLayout><ProductDetail /></MarketplaceLayout>} />
        <Route path="product/:productSlug" element={<ProductDetails />} />
        <Route path="withdraw" element={<MarketplaceLayout><Withdraw /></MarketplaceLayout>} />
        
        {/* Search */}
        <Route path="search" element={<MarketplaceLayout><MarketplaceSearch /></MarketplaceLayout>} />
        
        {/* Artist Routes */}
        <Route path="artist-registration" element={<MarketplaceLayout><ArtistRegistration /></MarketplaceLayout>} />
        <Route path="artist/:artistId" element={<ArtistProfile />} />

        {/* Protected Routes - User Access */}
        <Route path="cart" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout><Cart /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="wishlist" element={
          <AuthGuard requireAuth={true}>
            <Wishlist />
          </AuthGuard>
        } />
        <Route path="checkout" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout><Checkout /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="order-confirmation/:orderId" element={
          <MarketplaceLayout><OrderConfirmation /></MarketplaceLayout>
        } />
        <Route path="order-success" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout><OrderSuccess /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="payment-processing" element={
          <MarketplaceLayout><PaymentProcessing /></MarketplaceLayout>
        } />
        <Route path="payment-success" element={
          <MarketplaceLayout><PaymentSuccess /></MarketplaceLayout>
        } />
        <Route path="payment-failure" element={
          <MarketplaceLayout><PaymentFailure /></MarketplaceLayout>
        } />
        <Route path="orders" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout><OrderHistory /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="orders/:orderId" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout><OrderDetails /></MarketplaceLayout>
          </AuthGuard>
        } />
        
        {/* Security Routes - User Profile, MFA, and Session Management */}
        <Route path="profile" element={
          <AuthGuard requireAuth={true}>
            <ProfilePage />
          </AuthGuard>
        } />
        <Route path="security/mfa-setup" element={
          <AuthGuard requireAuth={true}>
            <MFASetup />
          </AuthGuard>
        } />
        <Route path="security/sessions" element={
          <AuthGuard requireAuth={true}>
            <SessionManager />
          </AuthGuard>
        } />
        
        {/* Payment API Credentials Management - Only for Artists */}
        <Route path="payment-credentials" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ARTIST}>
            <MarketplaceLayout><PaymentCredentialsManager /></MarketplaceLayout>
          </AuthGuard>
        } />

        {/* Admin Routes */}
        <Route path="admin/artist-requests" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ADMIN}>
            <MarketplaceLayout><ArtistRequestsDashboard /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="admin/tools" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ADMIN}>
            <MarketplaceLayout><AdminTools /></MarketplaceLayout>
          </AuthGuard>
        } />
        <Route path="admin/setup" element={
          <MarketplaceLayout><SuperAdminSetup /></MarketplaceLayout>
        } />
        <Route path="make-admin" element={
          <MarketplaceLayout><AdminSetupButton /></MarketplaceLayout>
        } />
        <Route path="direct-admin" element={
          <MarketplaceLayout><DirectAdminSetup /></MarketplaceLayout>
        } />
        <Route path="quick-admin" element={
          <MarketplaceLayout><QuickAdminSetup /></MarketplaceLayout>
        } />
        <Route path="get-user-id" element={
          <MarketplaceLayout><GetUserId /></MarketplaceLayout>
        } />
        <Route path="create-admin" element={
          <MarketplaceLayout><DirectAdminCreator /></MarketplaceLayout>
        } />
        <Route path="simple-admin" element={
          <MarketplaceLayout><SimpleAdminSetup /></MarketplaceLayout>
        } />
        <Route path="emergency-admin" element={
          <MarketplaceLayout><EmergencyAdminSetup /></MarketplaceLayout>
        } />
        <Route path="emergency-admin-access" element={
          <MarketplaceLayout><EmergencyAdminAccess /></MarketplaceLayout>
        } />
        <Route path="bypass-admin" element={
          <MarketplaceLayout><BypassAdminDashboard /></MarketplaceLayout>
        } />
        <Route path="direct-super-admin" element={
          <MarketplaceLayout><DirectSuperAdminAccess /></MarketplaceLayout>
        } />
        
        {/* Artist Routes */}
        <Route path="artist-dashboard" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ARTIST}>
            <MarketplaceLayout>
              <ArtistEarningsDashboard />
            </MarketplaceLayout>
          </AuthGuard>
        } />
        
        {/* New product creation route */}
        <Route path="create-product" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ARTIST}>
            <MarketplaceLayout>
              <ProductCreateForm />
            </MarketplaceLayout>
          </AuthGuard>
        } />
        
        {/* Product editing route */}
        <Route path="edit-product/:productId" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.ARTIST}>
            <MarketplaceLayout>
              <EditProduct />
            </MarketplaceLayout>
          </AuthGuard>
        } />
        
        {/* Redirect from old product creation route */}
        <Route path="products/create" element={
          <Navigate to="/marketplace/create-product" replace />
        } />
        
        <Route path="become-artist" element={
          <AuthGuard requireAuth={true}>
            <MarketplaceLayout>
              <ArtistRequestForm />
            </MarketplaceLayout>
          </AuthGuard>
        } />
        
        <Route path="artist-approval" element={
          <AuthGuard requireAuth={true} requiredRole={ROLES.SUPER_ADMIN}>
            <MarketplaceLayout>
              <ArtistApprovalDashboard />
            </MarketplaceLayout>
          </AuthGuard>
        } />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/marketplace" replace />} />
      </Routes>
    </HelmetProvider>
  );
};

export default MarketplaceRoutes;
